/**
 * Pure helpers for the event landing-page MVP (publish workflow + URL prefill).
 * Kept free of React/Supabase so they're unit-testable.
 */

export interface PrefillParams {
  email?: string;
  memberNumber?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
}

/**
 * Read prefill values from the landing-page URL. Invitees arrive with some of
 * email / member number / first name / last name / phone as query params. Accept
 * a few common spellings so links from different sources all work.
 */
export function parsePrefillParams(sp: URLSearchParams): PrefillParams {
  const first = (...keys: string[]): string | undefined => {
    for (const k of keys) {
      const v = sp.get(k);
      if (v && v.trim()) {
        return v.trim();
      }
    }
    return undefined;
  };
  return {
    email: first("email", "e")?.toLowerCase(),
    memberNumber: first("member_number", "membernumber", "memberNumber", "mn"),
    firstName: first("first_name", "firstname", "firstName", "fname"),
    lastName: first("last_name", "lastname", "lastName", "lname"),
    phone: first("phone", "phone_number", "telephone", "p"),
  };
}

export type PublishStatus =
  | "draft"
  | "ready"
  | "published"
  | "published_unavailable";

export interface PublishReadinessInput {
  slug: string | null | undefined;
  isPublished: boolean;
  eventTitle: string | null | undefined;
  eventStatus: string | null | undefined; // draft/scheduled/cancelled/completed/...
  registrationOpen: boolean | null | undefined; // events.registration_open
  hasStartTime: boolean;
  hasRegistrationList: boolean;
  noListAck: boolean; // operator chose "don't add registrants to a list"
}

export interface PublishReadiness {
  status: PublishStatus;
  blockers: string[]; // must be resolved before publishing
  warnings: string[]; // non-blocking cautions
}

const UNAVAILABLE_STATUSES = new Set(["cancelled", "canceled", "completed"]);

/**
 * Compute the publish status panel + what (if anything) blocks publishing.
 * Conservative on event status: only cancelled/completed + registration closed
 * make a page unusable — mirrors the public page and the edge function.
 */
export function evaluatePublishReadiness(
  i: PublishReadinessInput,
): PublishReadiness {
  const eventStatus = (i.eventStatus || "").toLowerCase();
  const eventUnusable =
    UNAVAILABLE_STATUSES.has(eventStatus) || i.registrationOpen === false;

  const blockers: string[] = [];
  if (!i.slug || !i.slug.trim()) {
    blockers.push("A URL slug is required.");
  }
  if (!i.eventTitle || !i.eventTitle.trim()) {
    blockers.push("The event needs a title.");
  }
  if (!i.hasStartTime) {
    blockers.push("The event needs a start date/time.");
  }
  if (UNAVAILABLE_STATUSES.has(eventStatus)) {
    blockers.push(`The event is ${eventStatus}.`);
  }
  if (i.registrationOpen === false) {
    blockers.push("Registration is closed for this event.");
  }
  if (!i.hasRegistrationList && !i.noListAck) {
    blockers.push(
      "Choose a registration list, or confirm you don't want to add registrants to a list.",
    );
  }

  const warnings: string[] = [];
  if (!i.hasRegistrationList) {
    if (i.isPublished) {
      // Always surfaced on a live page so the "no list" state survives reload
      // even when the operator's ack only lived in component state.
      warnings.push(
        "This page is published but registrants are NOT being added to any CRM list.",
      );
    } else if (i.noListAck) {
      warnings.push(
        "Registrants will NOT be added to any CRM list (you chose to skip the list).",
      );
    }
  }

  let status: PublishStatus;
  if (i.isPublished) {
    status = eventUnusable ? "published_unavailable" : "published";
  } else {
    status = blockers.length === 0 ? "ready" : "draft";
  }
  return { status, blockers, warnings };
}

/** Static lists are the only ones safe to receive list_contacts rows. */
export function isStaticList(list: { is_dynamic?: boolean | null }): boolean {
  return list.is_dynamic !== true;
}

/**
 * The event fields that decide whether the public landing page may refuse a registration
 * for being full.
 */
export interface LandingCapacityInput {
  max_attendees?: number | null;
  capacity?: number | null;
  attendance_hard_cap?: boolean | null;
}

/**
 * The people-limit the PUBLIC LANDING PAGE may enforce, or null when it may not refuse at all.
 *
 * ⚠️ This MUST stay equivalent to the server's expression in
 * `supabase/functions/event-landing-register/index.ts` ("4. Check capacity by PEOPLE"):
 *
 *     const maxCapacity = event.attendance_hard_cap ? (event.max_attendees || event.capacity) : event.capacity;
 *
 * The page previously used `max_attendees || capacity` with NO flag check, which made it
 * STRICTER than the endpoint it fronts: on a soft-capped event with `capacity` NULL and
 * `max_attendees` 77, the server was uncapped while the page showed SOLD OUT at 77 and
 * refused registrations the server would have accepted. A refusal here never reaches the
 * server's `outcome_code='capacity'` logger, so those turn-aways left no trace.
 *
 * `max_attendees` is a HARD limit only when `attendance_hard_cap` is set; otherwise only the
 * separate `capacity` column applies. Returning null means "do not refuse" — the server
 * remains the authority, and the soft pre-registration cap is enforced there.
 */
export function landingPageEnforceableCapacity(
  event: LandingCapacityInput,
): number | null {
  const cap = event.attendance_hard_cap
    ? (event.max_attendees ?? null) || (event.capacity ?? null)
    : (event.capacity ?? null);
  return cap ?? null;
}
